import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../../context/AuthContext";
import ArchieButton from "./ArchieButton";
import ArchieChatWindow from "./ArchieChatWindow";
import {
  useStartArchieChat,
  useContinueArchieChat,
  useLoadArchieConversation,
  useInvalidateArchieConversations,
} from "../../hooks/useArchieChat";
import useArchieLayout from "../../hooks/useArchieLayout";
import { ArchieChatMessage } from "../../types/archie/archie";

const SESSION_KEY = "archie_conversation_id";
const MESSAGES_KEY = "archie_messages";

/**
 * Extracts a /customers/... search URL from assistant message text.
 * Used when loading historical messages that don't have a separate searchUrl field.
 */
const extractSearchUrl = (text: string): string | undefined => {
  const match = /\/customers\/[^\s)\]"]+/.exec(text);
  return match ? match[0] : undefined;
};

const ArchieChatWidget = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const { user } = useContext(AuthContext);
  const userId = user?.userId;

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ArchieChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const prevCustomerIdRef = useRef<string | undefined>(undefined);

  const queryClient = useQueryClient();
  const startChat = useStartArchieChat();
  const continueChat = useContinueArchieChat();
  const loadConversation = useLoadArchieConversation();
  const invalidateConversations = useInvalidateArchieConversations();
  const isLoading = startChat.isPending || continueChat.isPending;
  const { bubblePosition, windowPosition, windowSize, handleBubbleDrag, handleResize } =
    useArchieLayout(isOpen);

  // Restore conversation from sessionStorage on mount
  useEffect(() => {
    const storedId = sessionStorage.getItem(SESSION_KEY);
    const storedMessages = sessionStorage.getItem(MESSAGES_KEY);

    if (storedId) {
      setConversationId(storedId);
    }
    if (storedMessages) {
      try {
        const parsed = JSON.parse(storedMessages) as ArchieChatMessage[];
        // Restore Date objects from JSON strings
        setMessages(
          parsed.map((m) => ({ ...m, timestamp: new Date(m.timestamp) }))
        );
      } catch {
        // Corrupted data — start fresh
        sessionStorage.removeItem(MESSAGES_KEY);
      }
    }
  }, []);

  // Persist conversation state to sessionStorage
  useEffect(() => {
    if (conversationId) {
      sessionStorage.setItem(SESSION_KEY, conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(MESSAGES_KEY, JSON.stringify(messages));
    }
  }, [messages]);

  // Clear conversation when switching to a *different* customer.
  // We intentionally skip when customerId becomes undefined (navigating
  // away from a customer page) so the history is preserved if the user
  // navigates back to the same customer.
  useEffect(() => {
    if (
      prevCustomerIdRef.current &&
      customerId &&
      prevCustomerIdRef.current !== customerId
    ) {
      setMessages([]);
      setConversationId(null);
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(MESSAGES_KEY);
    }
    if (customerId) {
      prevCustomerIdRef.current = customerId;
    }
  }, [customerId]);

  const handleSend = useCallback(
    (text: string) => {
      if (!customerId || !userId) return;

      // Add user message immediately
      const userMessage: ArchieChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        text,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      const onSuccess = (data: {
        conversationId: string;
        assistantText: string;
        searchUrl?: string;
      }) => {
        setConversationId(data.conversationId);

        const assistantMessage: ArchieChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          text: data.assistantText,
          timestamp: new Date(),
          searchUrl: data.searchUrl ?? undefined,
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Invalidate the conversations list so new conversations appear in history
        invalidateConversations(customerId, userId);

        // Refresh dashboard config in case Archie created a widget
        queryClient.invalidateQueries({ queryKey: ["dashboard-config", customerId] });
      };

      const onError = () => {
        const errorMessage: ArchieChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          text: "Sorry, something went wrong. Please try again.",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      };

      if (conversationId) {
        continueChat.mutate(
          {
            conversationId,
            request: { userText: text, customerId, userId, conversationId },
          },
          { onSuccess, onError }
        );
      } else {
        startChat.mutate(
          { userText: text, customerId, userId },
          { onSuccess, onError }
        );
      }
    },
    [customerId, userId, conversationId, startChat, continueChat, invalidateConversations, queryClient]
  );

  const handleLoadConversation = useCallback(
    (selectedConversationId: string) => {
      loadConversation.mutate(selectedConversationId, {
        onSuccess: (data) => {
          const loadedMessages: ArchieChatMessage[] = data.messages.map(
            (m) => ({
              id: m.messageId,
              role: m.role as "user" | "assistant",
              text: m.text,
              timestamp: new Date(m.createdAt),
              searchUrl:
                m.role === "assistant"
                  ? extractSearchUrl(m.text)
                  : undefined,
            })
          );
          setMessages(loadedMessages);
          setConversationId(data.conversationId);
          sessionStorage.setItem(SESSION_KEY, data.conversationId);
          sessionStorage.setItem(MESSAGES_KEY, JSON.stringify(loadedMessages));
        },
        onError: () => {
          const errorMessage: ArchieChatMessage = {
            id: crypto.randomUUID(),
            role: "assistant",
            text: "Sorry, I couldn't load that conversation. Please try again.",
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMessage]);
        },
      });
    },
    [loadConversation]
  );

  const handleNewConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(MESSAGES_KEY);
  }, []);

  // Only render when scoped to a customer with an authenticated user
  if (!customerId || !userId) return null;

  return (
    <>
      <ArchieButton
        bottom={bubblePosition.bottom}
        right={bubblePosition.right}
        onClick={() => setIsOpen((prev) => !prev)}
        onDrag={handleBubbleDrag}
      />
      <ArchieChatWindow
        open={isOpen}
        messages={messages}
        isLoading={isLoading}
        customerId={customerId}
        userId={userId}
        bottom={windowPosition.bottom}
        right={windowPosition.right}
        width={windowSize.width}
        height={windowSize.height}
        onSend={handleSend}
        onClose={() => setIsOpen(false)}
        onLoadConversation={handleLoadConversation}
        onNewConversation={handleNewConversation}
        onResize={handleResize}
      />
    </>
  );
};

export default ArchieChatWidget;
